import React, { useMemo } from 'react';
import styled, { useTheme, keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

import { formatDate } from '../../utils/Utils';
import { usePerformanceContext, okrAverageScore } from './config';

// P2 Faz 1 — Bileşen 2 (Bağlam Paneli). Yönetici 360 formunu doldururken /
// kalibrasyon kararı verirken sağ rayda görmesi gereken kanıtlar: dönem
// hedefleri, gelişim grafiği (kapalı dönem trendi), sürekli geri bildirimler,
// açık PIP/IDP rozeti. KENDİ VERİSİNİ KENDİ ÇEKER — çağıranın yalnız iki id
// vermesi yeterlidir, böylece hem FillFeedbackModal hem CalibrationDecisionModal
// hiçbir veri borusu kurmadan monte edebilir.
//
// Renkler YALNIZ theme token'larından (hardcode YASAK — DistributionChartCard/
// TrendCard deseni). Rozet/blok arka planları token'ın 8 haneli alfa
// varyantından türetilir (ör. `${primary.main}14`), böylece palet tek
// kaynaktan (colors.js) yönetilmeye devam eder.
//
// Toast YOK: hata/kapsam durumları sessizce "bilgi yok" metnine düşer
// (config/index.js'teki skipErrorToast ile birlikte).

const shimmer = keyframes`
  0% { opacity: 0.45; }
  50% { opacity: 0.85; }
  100% { opacity: 0.45; }
`;

const Panel = styled.aside`
  background: ${({ theme }) => theme.colors.surface.card};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: 10px;
  padding: 16px 18px;
`;

const PanelTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PanelMeta = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 2px;
`;

const Section = styled.div`
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.colors.surface.border};
`;

const SectionTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 10px;
`;

const EmptyText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => `${p.$tone}1A`};
  color: ${(p) => p.$tone};
`;

const GoalRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;

  & + & {
    border-top: 1px dashed ${({ theme }) => theme.colors.surface.border};
  }
`;

const GoalMain = styled.div`
  min-width: 0;
`;

const GoalTypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ theme }) => `${theme.colors.primary.main}14`};
  color: ${({ theme }) => theme.colors.primary.main};
  margin-bottom: 4px;
`;

const GoalTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  word-break: break-word;
`;

const GoalScore = styled.div`
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.secondary.dark};
  white-space: nowrap;
`;

const FeedbackRow = styled.div`
  background: ${({ theme }) => `${theme.colors.primary.main}0A`};
  border-radius: 6px;
  padding: 8px 10px;

  & + & {
    margin-top: 8px;
  }
`;

const FeedbackHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const FeedbackCategory = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${(p) => p.$tone};
`;

const FeedbackDate = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const FeedbackText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: 4px;
  word-break: break-word;
`;

const SkeletonBar = styled.div`
  height: ${(p) => p.$h || 12}px;
  width: ${(p) => p.$w || '100%'};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.surface.border};
  animation: ${shimmer} 1.2s ease-in-out infinite;

  & + & {
    margin-top: 8px;
  }
`;

function LoadingSkeleton() {
  return (
    <div aria-busy="true">
      <SkeletonBar $h={14} $w="55%" />
      <SkeletonBar $h={10} $w="80%" />
      <SkeletonBar $h={10} $w="70%" />
      <SkeletonBar $h={64} />
      <SkeletonBar $h={10} $w="60%" />
      <SkeletonBar $h={10} $w="85%" />
    </div>
  );
}

export default function PerformanceContextPanel({ periodId, employeeId }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { context, fetching } = usePerformanceContext(periodId, employeeId);

  const trendData = useMemo(() => (context?.history || []).map((item) => ({
    name: item.periodName,
    systemAScore: item.systemAScore != null ? Number(item.systemAScore) : null,
    weightedOverall: item.weightedOverall != null ? Number(item.weightedOverall) : null,
  })), [context]);

  const hasSystemA = trendData.some((d) => d.systemAScore != null);
  const hasSystemB = trendData.some((d) => d.weightedOverall != null);

  const categoryTone = (category) => (category === 'development_area'
    ? theme.colors.auxiliar.warning
    : theme.colors.secondary.dark);

  return (
    <Panel>
      <PanelTitle>{t('performance.context.title')}</PanelTitle>
      {context?.employeeName && <PanelMeta>{context.employeeName}</PanelMeta>}

      {fetching && <Section><LoadingSkeleton /></Section>}

      {!fetching && !context && (
        <Section><EmptyText>{t('performance.context.notAvailable')}</EmptyText></Section>
      )}

      {!fetching && context && (
        <>
          {/* Açık süreç rozetleri — PIP/IDP tek satır, detay YOK. */}
          {(context.activePipStatus || context.activeIdpStatus) ? (
            <BadgeRow>
              {context.activePipStatus && (
                <Badge $tone={theme.colors.auxiliar.danger}>
                  {t('performance.context.pipLabel')}
                  {' · '}
                  {t(`performance.pip.status.${context.activePipStatus}`, { defaultValue: context.activePipStatus })}
                </Badge>
              )}
              {context.activeIdpStatus && (
                <Badge $tone={theme.colors.auxiliar.purple}>
                  {t('performance.context.idpLabel')}
                  {' · '}
                  {t(`performance.idp.status.${context.activeIdpStatus}`, { defaultValue: context.activeIdpStatus })}
                </Badge>
              )}
            </BadgeRow>
          ) : (
            <BadgeRow><EmptyText>{t('performance.context.plansEmpty')}</EmptyText></BadgeRow>
          )}

          <Section>
            <SectionTitle>{t('performance.context.goalsTitle')}</SectionTitle>
            {(context.goals || []).length === 0 ? (
              <EmptyText>{t('performance.goal.noGoals')}</EmptyText>
            ) : (
              context.goals.map((goal) => {
                const okrScore = okrAverageScore(goal);
                const score = goal.goalType === 'kpi'
                  ? (goal.completionPct != null ? `%${goal.completionPct}` : '-')
                  : (okrScore != null ? `${okrScore} / 5` : '-');
                return (
                  <GoalRow key={goal.id}>
                    <GoalMain>
                      <GoalTypeBadge>
                        {t(`performance.goal.goalType.${goal.goalType}`, { defaultValue: goal.goalType })}
                      </GoalTypeBadge>
                      <GoalTitle>{goal.title}</GoalTitle>
                    </GoalMain>
                    <GoalScore>{score}</GoalScore>
                  </GoalRow>
                );
              })
            )}
          </Section>

          <Section>
            <SectionTitle>{t('performance.context.trendTitle')}</SectionTitle>
            {trendData.length === 0 ? (
              <EmptyText>{t('performance.context.trendEmpty')}</EmptyText>
            ) : (
              <ResponsiveContainer width="100%" height={170}>
                <LineChart data={trendData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.surface.border} vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: theme.colors.text.muted }}
                    axisLine={{ stroke: theme.colors.surface.border }}
                    tickLine={false}
                  />
                  {/* İki seri farklı ölçekte (Sistem A 0-100, 360° 1-5) — TrendCard
                      ile aynı çözüm: ayrı yAxisId, normalize etme YOK. */}
                  <YAxis yAxisId="a" hide domain={[0, 100]} />
                  <YAxis yAxisId="b" hide domain={[0, 5]} orientation="right" />
                  <Tooltip cursor={{ stroke: theme.colors.surface.border }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {hasSystemA && (
                    <Line
                      yAxisId="a"
                      type="monotone"
                      dataKey="systemAScore"
                      name={t('performance.results.trendSystemA')}
                      stroke={theme.colors.primary.main}
                      strokeWidth={2}
                      dot={{ r: 3, fill: theme.colors.primary.main }}
                      connectNulls
                    />
                  )}
                  {hasSystemB && (
                    <Line
                      yAxisId="b"
                      type="monotone"
                      dataKey="weightedOverall"
                      name={t('performance.results.trendSystemB')}
                      stroke={theme.colors.auxiliar.purple}
                      strokeWidth={2}
                      dot={{ r: 3, fill: theme.colors.auxiliar.purple }}
                      connectNulls
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            )}
          </Section>

          <Section>
            <SectionTitle>{t('performance.calibration.continuousFeedbackTitle')}</SectionTitle>
            {(context.feedback || []).length === 0 ? (
              <EmptyText>{t('performance.context.feedbackEmpty')}</EmptyText>
            ) : (
              context.feedback.map((ref, i) => (
                // Gönderen kimliği BE DTO'sunda tip düzeyinde YOK — burada da
                // hiçbir "kim gönderdi" alanı render EDİLMEZ.
                <FeedbackRow key={i}>
                  <FeedbackHead>
                    <FeedbackCategory $tone={categoryTone(ref.category)}>
                      {t(`performance.calibration.continuousFeedback.category.${ref.category}`, { defaultValue: ref.category })}
                    </FeedbackCategory>
                    <FeedbackDate>{formatDate(ref.createDate, i18n.language)}</FeedbackDate>
                  </FeedbackHead>
                  {ref.text && <FeedbackText>{ref.text}</FeedbackText>}
                </FeedbackRow>
              ))
            )}
          </Section>
        </>
      )}
    </Panel>
  );
}
